import React, { useMemo } from 'react'
import {
  Avatar,
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import ForumIcon from '@mui/icons-material/Forum'
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread'
import moment from 'moment'
import ResourceChat from './ResourceChat'

const statusColors = {
  Available: '#00964C',
  'Mobile & Available': '#157746',
  Assigned: '#E32525',
  Acknowledged: '#C2410C',
  'On Route': '#9A3412',
  'At Scene': '#CCA43B',
  'On Scene': '#CCA43B',
  Break: '#1F3D7C',
  Unavailable: '#85276A',
  'Off Duty': '#475569',
}

// Right-hand drawer listing every resource conversation for the control
// room. Selecting one (or arriving preselected from a resource card/drawer)
// opens the shared resource thread.
const ChatDrawer = ({
  open,
  onClose,
  resources = [],
  summaries = [],
  selectedResourceID,
  onSelectResource,
  refreshSummaries,
}) => {
  const summariesByResource = useMemo(() => {
    const map = new Map()
    summaries.forEach((summary) => map.set(String(summary.resource), summary))
    return map
  }, [summaries])

  const selectedResource = resources.find(
    (resource) =>
      String(resource._id ?? resource.id) === String(selectedResourceID),
  )

  const totalUnread = useMemo(
    () =>
      summaries.reduce(
        (total, summary) => total + (summary.unreadFromCrew ?? 0),
        0,
      ),
    [summaries],
  )

  const sortedResources = useMemo(() => {
    return [...resources].sort((a, b) => {
      const aSummary = summariesByResource.get(String(a._id ?? a.id))
      const bSummary = summariesByResource.get(String(b._id ?? b.id))
      const aUnread = aSummary?.unreadFromCrew ?? 0
      const bUnread = bSummary?.unreadFromCrew ?? 0
      if (aUnread !== bUnread) return bUnread - aUnread
      const aAt = aSummary?.lastMessageAt
        ? new Date(aSummary.lastMessageAt).getTime()
        : 0
      const bAt = bSummary?.lastMessageAt
        ? new Date(bSummary.lastMessageAt).getTime()
        : 0
      if (aAt !== bAt) return bAt - aAt
      return (a.callSign || '').localeCompare(b.callSign || '', undefined, {
        numeric: true,
      })
    })
  }, [resources, summariesByResource])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          p: 1.5,
          background:
            'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
          color: 'white',
        }}
      >
        {selectedResource ? (
          <IconButton
            size="small"
            onClick={() => onSelectResource(null)}
            sx={{ color: 'white' }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <ForumIcon sx={{ ml: 0.5 }} />
        )}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={900} noWrap color="white">
            {selectedResource ? selectedResource.callSign : 'Resource messages'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {selectedResource
              ? 'Everyone on this resource and in the control room sees this chat'
              : totalUnread
                ? `${totalUnread} unread message${totalUnread === 1 ? '' : 's'} across your resources`
                : 'Conversations with your operational resources'}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {selectedResource ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ResourceChat
            resourceID={selectedResource._id ?? selectedResource.id}
            resourceCallSign={selectedResource.callSign}
            onMessagesViewed={refreshSummaries}
            height="flex"
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1.5,
            backgroundColor: '#F1F5F9',
          }}
        >
          {sortedResources.length === 0 && (
            <Stack alignItems="center" spacing={1.5} py={6}>
              <Avatar sx={{ width: 56, height: 56, backgroundColor: '#E2E8F0' }}>
                <ForumIcon sx={{ color: '#94A3B8' }} />
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                No resources in this control room yet.
              </Typography>
            </Stack>
          )}
          <Stack spacing={1}>
            {sortedResources.map((resource) => {
              const resourceID = String(resource._id ?? resource.id)
              const summary = summariesByResource.get(resourceID)
              const unread = summary?.unreadFromCrew ?? 0
              const statusColor = statusColors[resource.status] || '#268EBB'
              const crewCount = resource.users?.length ?? 0
              return (
                <Stack
                  key={resourceID}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  onClick={() => onSelectResource(resourceID)}
                  sx={{
                    p: 1.25,
                    borderRadius: 3,
                    cursor: 'pointer',
                    backgroundColor: unread ? '#FFFFFF' : 'rgba(255,255,255,0.75)',
                    border: unread
                      ? '1px solid rgba(227,37,37,0.35)'
                      : '1px solid #E2E8F0',
                    boxShadow: unread
                      ? '0 8px 20px rgba(227,37,37,0.12)'
                      : '0 2px 8px rgba(15,23,42,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 10px 24px rgba(15,23,42,0.14)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: unread ? '#E32525' : statusColor,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      fontSize: 13,
                      fontWeight: 900,
                      backgroundColor: statusColor,
                      boxShadow: `0 6px 14px ${statusColor}55`,
                    }}
                  >
                    {(resource.callSign || '?').slice(0, 3)}
                  </Avatar>

                  <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={900} noWrap>
                        {resource.callSign}
                      </Typography>
                      <Chip
                        label={resource.status || 'Unknown'}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 800,
                          borderRadius: 999,
                          backgroundColor: `${statusColor}1A`,
                          color: statusColor,
                        }}
                      />
                      <Box sx={{ flex: 1 }} />
                      {summary?.lastMessageAt && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: unread ? '#E32525' : 'text.secondary',
                            fontWeight: unread ? 800 : 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {moment(summary.lastMessageAt).fromNow(true)}
                        </Typography>
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: unread ? '#111827' : 'text.secondary',
                        fontWeight: unread ? 700 : 400,
                      }}
                    >
                      {summary?.lastMessage ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 800 }}>
                            {summary.lastFromControl
                              ? 'Control'
                              : summary.lastAuthorName || resource.callSign}
                            :
                          </Box>{' '}
                          {summary.lastMessage}
                        </>
                      ) : (
                        `Start a conversation${crewCount ? ` with ${crewCount} crew member${crewCount === 1 ? '' : 's'}` : ''}`
                      )}
                    </Typography>
                  </Stack>

                  {unread > 0 && (
                    <Stack alignItems="center" spacing={0.25}>
                      <Avatar
                        sx={{
                          width: 26,
                          height: 26,
                          fontSize: 12,
                          fontWeight: 900,
                          backgroundColor: '#E32525',
                        }}
                      >
                        {unread}
                      </Avatar>
                      <MarkChatUnreadIcon
                        sx={{ fontSize: 13, color: '#E32525' }}
                      />
                    </Stack>
                  )}
                </Stack>
              )
            })}
          </Stack>
        </Box>
      )}
    </Drawer>
  )
}

export default ChatDrawer
